import React, { useEffect, useMemo, useRef, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@shared/libs/ckeditor";
import "ckeditor5/ckeditor5.css";
import "../../styles/ckeditor.css";
import "../../styles/fileblockwidget.css";
import { global, globalMenu, useSnEditorStore } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import axios from "axios";

interface Props {
  uniqueId: string;
  selectedData: any;
  body: string;
  setBody: (val: string) => void;
  /** dc_editcon 등으로 계산된 읽기전용 여부. true면 CKEditor 편집 잠금. */
  isReadOnly?: boolean;
}

// 이미지 업로드 어댑터
function uploadAdapter(loader: any) {
  return {
    upload: () => {
      return new Promise((resolve, reject) => {
        (async () => {
          try {
            const file = await loader.file;
            const formData = new FormData();
            formData.append("uploads", file);

            const fakeUrl = URL.createObjectURL(file);
            resolve({ default: fakeUrl });
          } catch (error) {
            reject(error);
          }
        })();
      });
    },
  };
}

// 이미지 업로드 핸들러 등록
function uploadPlugin(editor: any) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) =>
    uploadAdapter(loader);
}

const SnEditor: React.FC<Props> = ({
  uniqueId,
  selectedData,
  body,
  setBody,
  isReadOnly = false,
}) => {
  const { register, unregister } = useSnEditorStore();
  const editorContainerRef = useRef<any>();
  const editorRef = useRef<any>();
  const fileInputRef = useRef<any>();
  const selectedDataRef = useRef(selectedData);
  // 드롭 핸들러는 onReady에서 1회만 등록되므로, 최신 읽기전용 상태를 ref로 참조한다.
  const isReadOnlyRef = useRef(isReadOnly);
  useEffect(() => {
    isReadOnlyRef.current = isReadOnly;
  }, [isReadOnly]);

  const [isLayoutReady, setIsLayoutReady] = useState(false);

  const cdModule = globalMenu.getState().activeMenuInfo.cd_module || "";

  useEffect(() => {
    setIsLayoutReady(true);
    return () => setIsLayoutReady(false);
  }, []);

  useEffect(() => {
    register(uniqueId, () => {
      fileInputRef.current?.click();
    });
    return () => {
      unregister(uniqueId);
    };
  }, [uniqueId]);

  useEffect(() => {
    selectedDataRef.current = selectedData;
  }, [selectedData]);

  useEffect(() => {
    const editorRoot = editorContainerRef.current;

    const handleClick = async (e: any) => {
      const target = e.target as HTMLElement;

      if (target.closest(".file-widget-download")) {
        const widgetEl = target.closest(".file-widget") as HTMLElement;
        const fileUrl = widgetEl.dataset.fileUrl;
        const fileName = widgetEl.dataset.fileName;

        if (!fileUrl) return;

        try {
          const params = {
            dc_url: fileUrl,
          };
          const res = await fetch("/snotes/file/download2", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
            },
            body: JSON.stringify(params),
          });
          if (!res.ok) throw new Error("다운로드 실패");

          const blob = await res.blob();
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.download = fileName || "download";
          document.body.appendChild(link);
          link.click();

          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (err) {
          alert("파일 다운로드 실패");
        }
      }
    };

    editorRoot?.addEventListener("click", handleClick);
    return () => {
      editorRoot?.removeEventListener("click", handleClick);
    };
  }, [isLayoutReady]);

  const { editorConfig } = useMemo(() => {
    if (!isLayoutReady) return {};

    return {
      editorConfig: {
        licenseKey: "GPL",
        language: "ko",
        placeholder: "내용을 입력해 주세요",
        toolbar: {
          items: [
            "undo",
            "redo",
            "|",
            "fontFamily",
            "fontSize",
            "fontColor",
            "fontBackgroundColor",
            "|",
            "bold",
            "italic",
            "underline",
            "strikethrough",
            "|",
            "alignment",
            "|",
            "link",
            "fileUpload",
            "imageInsert",
            "insertTable",
            "|",
            "bulletedList",
            "numberedList",
            "todoList",
            "|",
            "outdent",
            "indent",
          ],
          shouldNotGroupWhenFull: true,
        },
        fileUpload: { uniqueId },
        htmlSupport: {
          allow: [
            {
              name: "span",
              classes: ["custom-download-link"],
              attributes: {
                "data-name": true as true,
                "data-url": true as true,
              },
            },
          ],
        },
        extraPlugins: [uploadPlugin],
      },
    };
  }, [isLayoutReady]);

  const handleFileUpload = async (e: any) => {
    const fileList = Array.from(e.target.files) as File[];
    if (!fileList.length) return;

    // 이미지 파일과 그 외 파일 구분
    const imageFiles = fileList.filter((file) =>
      file.type.startsWith("image/")
    );
    const nonImageFiles = fileList.filter(
      (file) => !file.type.startsWith("image/")
    );

    const editor = editorRef.current;
    if (!editor) return;

    // 이미지 파일은 CKEditor 기본 이미지 업로드 플러그인으로 처리
    if (imageFiles.length) {
      for (const file of imageFiles) {
        const reader = new FileReader();

        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        editor.model.change((writer: any) => {
          const imageElement = writer.createElement("imageBlock", {
            src: base64,
            alt: file.name,
          });
          console.log(file.name);
          const insertPosition =
            editor.model.document.selection.getLastPosition();
          editor.model.insertContent(imageElement, insertPosition);

          // 삽입한 이미지 뒤로 커서 이동
          writer.setSelection(imageElement, "after");
        });
      }
    }

    // 비이미지 파일 업로드 후 파일블록 삽입
    if (nonImageFiles.length) {
      const formDataFiles = new FormData();
      nonImageFiles.forEach((file) => formDataFiles.append("files", file));
      formDataFiles.append(
        "filePath",
        `${cdModule}/ISS/${selectedDataRef.current}`
      );

      try {
        const res = await fetch("/snotes/file/upload2", {
          method: "POST",
          headers: {
            "Content-Type": "multipart/form-data",
            "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
          },
          body: formDataFiles,
        });

        if (!res.ok) throw new Error("파일 업로드 실패");

        const attachments = await res.json();

        editorRef.current?.execute("insertFileBlockWidget", {
          attachments: attachments.map((file: any) => ({
            fileName: file.nm_file,
            fileUrl: file.dc_url,
          })),
        });
      } catch (err) {
        console.error(err);
        alert("파일 업로드 실패");
      }
    }

    // 에디터 포커스 & input 초기화
    editorRef.current?.editing.view.focus();
    e.target.value = "";
  };

  return (
    <div className={`main-container${isReadOnly ? " sn-editor-readonly" : ""}`}>
      <div className="editor-container editor-container_classic-editor">
        <div className="editor-container__editor" ref={editorContainerRef}>
          <div>
            {isLayoutReady && (
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={body}
                disabled={isReadOnly}
                onReady={(editor) => {
                  editorRef.current = editor;

                  const root = editor.model.document.getRoot();
                  if (!root) return;

                  const lastPosition = editor.model.createPositionAt(
                    root,
                    "end"
                  );
                  editor.model.change((writer) => {
                    writer.setSelection(lastPosition);
                  });

                  // 드래그 앤 드롭 이벤트 처리
                  const editorElement = editor.ui.view.editable.element;
                  if (!editorElement) return;

                  const handleDragOver = (e: DragEvent) => {
                    // 읽기전용이면 드롭을 허용하지 않는다(preventDefault 안 함 → 드롭불가 커서).
                    if (isReadOnlyRef.current) return;
                    e.preventDefault();
                    e.stopPropagation();
                  };

                  const handleDrop = async (e: DragEvent) => {
                    // 읽기전용이면 이미지/파일을 추가하지 않는다(브라우저 기본 동작도 막음).
                    if (isReadOnlyRef.current) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    e.preventDefault();
                    e.stopPropagation();

                    const files = Array.from(
                      e.dataTransfer?.files || []
                    ) as File[];
                    if (!files.length) return;

                    const imageFiles = files.filter((file) =>
                      file.type.startsWith("image/")
                    );
                    const nonImageFiles = files.filter(
                      (file) => !file.type.startsWith("image/")
                    );

                    const editor = editorRef.current;
                    if (!editor) return;

                    // 1. 이미지 파일 삽입(base64)
                    for (const file of imageFiles) {
                      const reader = new FileReader();

                      const base64 = await new Promise<string>(
                        (resolve, reject) => {
                          reader.onload = () =>
                            resolve(reader.result as string);
                          reader.onerror = reject;
                          reader.readAsDataURL(file);
                        }
                      );

                      editor.model.change((writer: any) => {
                        const imageElement = writer.createElement(
                          "imageBlock",
                          {
                            src: base64,
                            alt: file.name,
                          }
                        );

                        const insertPosition =
                          editor.model.document.selection.getLastPosition();
                        editor.model.insertContent(
                          imageElement,
                          insertPosition
                        );
                        writer.setSelection(imageElement, "after");
                      });
                    }

                    // 2. 일반 파일 처리
                    if (nonImageFiles.length) {
                      const formData = new FormData();
                      nonImageFiles.forEach((file) =>
                        formData.append("files", file)
                      );
                      formData.append(
                        "filePath",
                        `${cdModule}/ISS/${selectedDataRef.current}`
                      );

                      try {
                        // const res = await fetch("/snotes/file/upload2", {
                        //   method: "POST",
                        //   headers: {
                        //     "Content-Type": "multipart/form-data",
                        //     "X-TENANT-ID":
                        //       global.getState().userInfo.tenant_id || "DEFAULT",
                        //   },
                        //   body: formData,
                        // });
                        const res = await axios.post(
                          "/snotes/file/upload2",
                          formData,
                          {
                            headers: {
                              "Content-Type": "multipart/form-data",
                              "X-TENANT-ID":
                                global.getState().userInfo.tenant_id ||
                                "DEFAULT",
                            },
                          }
                        );

                        if (res.data.result) {
                          const if_status = res.data.result.if_status;
                          if (if_status === "E") throw new Error("업로드 실패");
                        }

                        const attachments: any[] = res.data;
                        editor.execute("insertFileBlockWidget", {
                          attachments: attachments.map((file: any) => ({
                            fileName: file.nm_file,
                            fileUrl: file.dc_url,
                          })),
                        });
                      } catch (err) {
                        console.error(err);
                        await showDialog("alter", "파일 업로드 실패.");
                      }
                    }

                    editor.editing.view.focus();
                  };

                  // 반드시 capture 단계로 등록
                  editorElement.addEventListener(
                    "dragover",
                    handleDragOver,
                    true
                  );
                  editorElement.addEventListener("drop", handleDrop, true);
                }}
                onBlur={(event, editor) => {
                  const data = editor.getData();
                  setBody(data);
                }}
              />
            )}
            <input
              id={`ck-file-input-${uniqueId}`}
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SnEditor);
